

/** @jsxImportSource react */
import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Mail, LogOut, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { auth } from '../services/firebase';
import { sendEmailVerification, reload } from 'firebase/auth';
import { useAuth } from '../contexts/auth';
import { useTranslation } from '../i18n';
import { Button } from './ui/Button';
import { toast } from './ui/Toast';

export const EmailVerificationModal = () => {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const [isChecking, setIsChecking] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    const resendMutation = useMutation({
        mutationFn: async () => {
            if (auth.currentUser) {
                // IMPORTANT: Reload user to get latest auth state (e.g., new email address)
                // before sending verification.
                await reload(auth.currentUser);
                await sendEmailVerification(auth.currentUser);
            } else {
                throw new Error("No user found");
            }
        },
        onSuccess: () => {
            toast.success(t('verificationEmailSent'));
            setEmailSent(true);
        },
        onError: (error: any) => {
            if (error.code === 'auth/too-many-requests') {
                toast.error(t('tooManyRequestsError'));
            } else {
                toast.error(t('unexpectedError'));
            }
        }
    });

    // Automatically try to send a verification email when the modal appears.
    useEffect(() => {
        // A small delay to avoid spamming on rapid re-renders.
        const timer = setTimeout(() => {
            if (!emailSent) {
                resendMutation.mutate();
            }
        }, 1000);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run ONLY once on mount

    // Polling to check if email is verified
    useEffect(() => {
        const intervalId = setInterval(async () => {
            if (auth.currentUser && !auth.currentUser.emailVerified) {
                setIsChecking(true);
                try {
                    await reload(auth.currentUser);
                    if (auth.currentUser.emailVerified) {
                        // AuthProvider will handle DB updates and cleanup via onAuthStateChanged
                        toast.success(t('emailVerifiedSuccess'));
                        clearInterval(intervalId);
                    }
                } catch (error) {
                    console.error("Error reloading auth user", error);
                } finally {
                    setIsChecking(false);
                }
            } else if (auth.currentUser?.emailVerified) {
                 clearInterval(intervalId);
            }
        }, 3000); // Check every 3 seconds

        return () => clearInterval(intervalId);
    }, [t]);

    if (!user) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-modal-in">
                {/* Header */}
                <div className="bg-primary-50 p-6 text-center border-b border-primary-100">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-primary-600 ring-4 ring-primary-100">
                        <Mail size={32} />
                    </div>
                    <h2 className="text-2xl font-bold text-secondary-900 mb-2">
                        {t('verificationNeeded')}
                    </h2>
                    <p className="text-secondary-600 text-sm">
                        {t('pleaseVerifyEmailMessage', { email: user.email })}
                    </p>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {emailSent ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-emerald-800">
                               {t('checkInboxAndSpam')}
                            </p>
                        </div>
                    ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-800">
                               {t('verifyAccountPrompt')}
                            </p>
                        </div>
                    )}


                    <div className="space-y-3">
                         <Button 
                            onClick={() => resendMutation.mutate()} 
                            disabled={resendMutation.isPending} 
                            fullWidth
                            variant="primary"
                            isLoading={resendMutation.isPending}
                        >
                            {t('resendVerificationEmail')}
                        </Button>
                        
                        <Button 
                            onClick={logout} 
                            variant="outline" 
                            fullWidth
                            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
                        >
                            <LogOut className="w-4 h-4 me-2" />
                            {t('logout')}
                        </Button>
                    </div>

                    {/* Status Indicator */}
                    <div className="flex items-center justify-center gap-2 text-xs text-secondary-400 pt-2 border-t border-secondary-100">
                        {isChecking ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        )}
                        <span>{t('checkingStatus', { defaultValue: "Checking verification status automatically..." })}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
